/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useEffect, useMemo, useState, Fragment } from 'react'
import { withTranslation } from 'react-i18next'
import styles from './PermitsList.module.scss'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { editPlateNumberAction, deletePermitAction, editFromConfirmationAction } from 'redux/actions'
import { parsePeriodText } from '../../../../redux/reducers/apiReducers/processMasterNumberReducer'
import moment from 'moment'

const formatDate = (dateStr, language) => {
  return moment(dateStr).locale(language).format('DD-MMM-YYYY')
}

const EXTRA_FEE_LABEL_KEYS = {
  EE: 'permits_list.ev_levy_electric',
  EH: 'permits_list.ev_levy_plug_in_hybrid',
  EEP: 'permits_list.ev_levy_electric_personalized',
  EHP: 'permits_list.ev_levy_plug_in_hybrid_personalized',
  46: 'permits_list.annual_retention_fee',
  51: 'permits_list.hst',
  BL: 'permits_list.buy_local_donation_fee',
  GD: 'permits_list.gaelic_donation_fee',
  MD: 'permits_list.mikmaw_youth_fund_fee',
  FD: 'permits_list.community_fund_donation_fee',
  IF: 'permits_list.infrastructure_fund_fee',
}

const getExtraFeeLabel = (extraFee, t) => {
  const labelKey = EXTRA_FEE_LABEL_KEYS[extraFee.extraFeeType]
  return labelKey ? t(labelKey) : extraFee.feeDescription
}

const PermitsList = ({ t, i18n, setIsError, reviewConfirmation = false }) => {
  let history = useHistory()
  const dispatch = useDispatch()

  const permits = useSelector((state) => state.app.permits)

  const [windowSize, setWindowSize] = useState(window.innerWidth)

  useEffect(() => {
    const listener = () => {
      setWindowSize(window.innerWidth)
    }
    window.addEventListener('resize', listener)

    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [])

  useEffect(() => {
    try {
      if (permits.length === 0) {
        history.push(`/${i18n.language}${t('routes.your_info')}`)
      }
      permits.forEach((permitInfo) => {
        if (!isValidPermit(permitInfo)) {
          dispatch(deletePermitAction(permitInfo.plateNumber))
        }
      })
    } catch (e) {
      console.log(e)
      setIsError(true)
    }
  }, [permits])

  const isValidPermit = (permitInfo) => {
    return permitInfo && permitInfo.plateNumber && permitInfo.vinSerialNumber
  }

  const getPermitOwingSum = (type) => {
    try {
      return permits.reduce(function (a, b) {
        return a + parseFloat(b[type]).toFixed(2)
      }, 0)
    } catch (e) {
      console.log(e)
    }
  }

  const getAmountString = (num) => {
    try {
      return i18n.language === 'en' ? `$${num}` : `${num.toString().replace('.', ',')} $`
    } catch (e) {
      console.log(e)
    }
  }

  const permitTotalFee = useMemo(() => {
    const amt = permits.reduce((prevVal, currentPermit) => prevVal + parseInt(currentPermit.renewalAmount), 0)
    return getAmountString((amt * 0.01).toFixed(2))
  }, [permits, i18n.language])

  const isMobile = useMemo(() => {
    return windowSize <= 768
  }, [windowSize])

  return (
    <div className="row">
      <div className="col-xs-12 col-md-10">
        <div className={styles['overflow_mobile']}>
          {permits.map((permitInfo, i) => {
            if (isMobile) {
              return (
                <MobilePermitsRow
                  key={i}
                  isMobile={isMobile}
                  permitInfo={permitInfo}
                  t={t}
                  i18n={i18n}
                  setIsError={setIsError}
                  getAmountString={getAmountString}
                  reviewConfirmation={reviewConfirmation}
                  rowNumber={i}
                />
              )
            }
            return (
              <PermitsRow
                key={i}
                isMobile={isMobile}
                permitInfo={permitInfo}
                t={t}
                i18n={i18n}
                setIsError={setIsError}
                getAmountString={getAmountString}
                reviewConfirmation={reviewConfirmation}
                rowNumber={i}
              />
            )
          })}

          {!reviewConfirmation && (
            <>
              <div>
                <strong>
                  {t('permits_list.total')}
                  {permitTotalFee}
                </strong>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const PermitsRow = ({ permitInfo, i18n, t, setIsError, getAmountString, reviewConfirmation, rowNumber, windowSize }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const permitEl = useRef(null)

  const permits = useSelector((state) => state.app.permits)
  // const [permitCounter, setPermitCounter] = useState(0)
  // console.log(rowNumber)

  // useEffect(() => {
  //   setPermitCounter(permits.length - 1)
  // }, [permits])

  const editPermitOnClick = () => {
    try {
      dispatch(editPlateNumberAction(permitEl.current.innerText))
      if (reviewConfirmation) {
        dispatch(editFromConfirmationAction(true))
      }
      history.push({
        pathname: `/${i18n.language}${t('routes.your_info')}`,
        state: {
          reviewConfirmation,
        },
      })
    } catch (e) {
      console.log(e)
      setIsError(true)
    }
  }

  const removePermitOnClick = () => {
    try {
      let confirm_text = window.confirm(t(`${t('are_you_sure')}`))
      if (confirm_text) {
        dispatch(deletePermitAction(permitEl.current.innerText))
        if (reviewConfirmation) {
          history.push(`/${i18n.language}${t('routes.review_confirmation')}`)
        } else {
          history.push(`/${i18n.language}${t('routes.listed_permits')}`)
        }
      }
    } catch (e) {
      console.log(e)
      setIsError(true)
    }
  }

  const renewalAmountStr = useMemo(() => {
    const extraFeesTotal = (permitInfo.extraFees || []).reduce((sum, extraFee) => sum + parseFloat(extraFee.extraFeeAmount), 0)
    return ((permitInfo.renewalAmount - extraFeesTotal) * 0.01).toFixed(2)
  }, [permitInfo.renewalAmount, permitInfo.extraFees])

  const motorVehicleAct = useMemo(() => {
    return i18n.language === 'en' ? (
      t('eligibility_question_farm.motor_vehicle_act')
    ) : (
      <i>{t('eligibility_question_farm.motor_vehicle_act')}</i>
    )
  }, [i18n.language])

  return (
    <Fragment>
      {/* {permits.length > 1 && ( */}
      <div className="row">
        <div className="col-xs-12 col-md-12">
          <h2>{t('review_confirmation.plate_renewal_info')}</h2>
          <div className={[styles['edit-remove-btn']]}>
            {permitInfo.paymentAmountFull ? null : (
              <button
                className={['btn-xs btn-default', styles['edit_button_right']].join(' ')}
                onClick={editPermitOnClick}
                value={t('permits_list.edit')}
                name={t('permits_list.edit')}
              >
                {t('permits_list.edit')}
              </button>
              // <a
              //   href="javascript:void(0);"
              //   className={[styles['edit_button_right']].join(' ')}
              //   onClick={editPermitOnClick}
              //   value={t('permits_list.edit')}
              //   name={t('permits_list.edit')}
              // >
              //   {t('permits_list.edit')}
              // </a>
            )}
            <button
              className={['btn-xs btn-secondary', styles['remove_button_right']].join(' ')}
              onClick={removePermitOnClick}
              value={t('permits_list.remove')}
              name={t('permits_list.remove')}
            >
              {t('permits_list.remove')}
            </button>
            {/* <a
                href={
                  reviewConfirmation ? `/${i18n.language}${t('routes.review_confirmation')}` : `/${i18n.language}${t('routes.listed_permits')}`
                }
                className={styles['remove_button_right']}
                onClick={removePermitOnClick}
                value={t('permits_list.remove')}
                name={t('permits_list.remove')}
              >
                {t('permits_list.remove')}
              </a> */}
          </div>
        </div>
      </div>
      {/* )} */}

      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.plate_number')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }} ref={permitEl}>
          {permitInfo.plateNumber}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.vin_serial')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {permitInfo.vinSerialNumber}
        </div>
      </div>
      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.renewal_period')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {parsePeriodText(permitInfo.renewalPeriod, i18n.language)}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.renewal_fee')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {getAmountString(renewalAmountStr, i18n.language)}
        </div>
      </div>
      {permitInfo.extraFees &&
        permitInfo.extraFees.map((extraFee, i) => (
          <div className="row row-odd" key={`extra-fee-${i}`}>
            <div className="col-xs-6">
              <strong>{getExtraFeeLabel(extraFee, t)}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {getAmountString((extraFee.extraFeeAmount * 0.01).toFixed(2), i18n.language)}
            </div>
          </div>
        ))}
      {permitInfo.insuranceCompany && (
        <div className="row row-odd">
          <div className="col-xs-6">
            <strong>{t('permits_list.insurance_company')}</strong>
          </div>
          <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
            {permitInfo.insuranceCompany.toUpperCase()}
          </div>
        </div>
      )}
      {permitInfo.policyNumber && (
        <div className="row row-even">
          <div className="col-xs-6">
            <strong>{t('permits_list.policy_number')}</strong>
          </div>
          <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
            {permitInfo.policyNumber}
          </div>
        </div>
      )}
      {permitInfo.effectiveDate && (
        <div className="row row-odd">
          <div className="col-xs-6">
            <strong>{t('permits_list.effective_date')}</strong>
          </div>
          <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
            {formatDate(permitInfo.effectiveDate, i18n.language)}
          </div>
        </div>
      )}
      {permitInfo.expiryDate && (
        <div className="row row-even">
          <div className="col-xs-6">
            <strong>{t('permits_list.expiry_date')}</strong>
          </div>
          <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
            {formatDate(permitInfo.expiryDate, i18n.language)}
          </div>
        </div>
      )}
      {permitInfo.vehicleType === 'fisherman' && (
        <Fragment>
          <div className="row row-odd">
            <div className="col-xs-6">
              <strong>{t('permits_list.commercial_aquaculture')}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {permitInfo.commercialAquaculture}
            </div>
          </div>
          <div className="row row-even">
            <div className="col-xs-6">
              <strong>{t('permits_list.fishing_vessel')}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {permitInfo.fishingVessel}
            </div>
          </div>
        </Fragment>
      )}
      {(permitInfo.vehicleType === 'fisherman' || permitInfo.vehicleType === 'farmer') && (
        <div className="row row-odd">
          <div className="col-xs-12">
            <strong>
              {t('permits_list.you_certified')}
              {motorVehicleAct}
              {permitInfo.vehicleType === 'farmer' ? t('permits_list.you_certified_farm') : null}
              {permitInfo.vehicleType === 'fisherman' ? t('permits_list.you_certified_fishing') : null}
            </strong>
          </div>
        </div>
      )}
    </Fragment>
  )
}

const MobilePermitsRow = ({ permitInfo, i18n, t, setIsError, getAmountString, reviewConfirmation, rowNumber, windowSize }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const permitEl = useRef(null)
  const permits = useSelector((state) => state.app.permits)

  const editPermitOnClick = () => {
    try {
      dispatch(editPlateNumberAction(permitEl.current.innerText))
      if (reviewConfirmation) {
        dispatch(editFromConfirmationAction(true))
      }
      history.push(`/${i18n.language}${t('routes.your_info')}`)
    } catch (e) {
      console.log(e)
      setIsError(true)
    }
  }

  const removePermitOnClick = () => {
    try {
      let confirm_text = window.confirm(t(`${t('are_you_sure')}`))
      if (confirm_text) {
        dispatch(deletePermitAction(permitEl.current.innerText))
        if (reviewConfirmation) {
          history.push(`/${i18n.language}${t('routes.review_confirmation')}`)
        } else {
          history.push(`/${i18n.language}${t('routes.listed_permits')}`)
        }
      }
    } catch (e) {
      console.log(e)
      setIsError(true)
    }
  }

  const renewalAmountStr = useMemo(() => {
    const extraFeesTotal = (permitInfo.extraFees || []).reduce((sum, extraFee) => sum + parseFloat(extraFee.extraFeeAmount), 0)
    return ((permitInfo.renewalAmount - extraFeesTotal) * 0.01).toFixed(2)
  }, [permitInfo.renewalAmount, permitInfo.extraFees])

  const motorVehicleAct = useMemo(() => {
    return i18n.language === 'en' ? (
      t('eligibility_question_farm.motor_vehicle_act')
    ) : (
      <i>{t('eligibility_question_farm.motor_vehicle_act')}</i>
    )
  }, [i18n.language])

  return (
    <Fragment>
      {/* {permits.length > 1 && ( */}
      <div className="row">
        <div className="col-xs-12 col-md-10" style={{ display: 'flex', alignItems: 'baseline' }}>
          <h2>{t('review_confirmation.plate_renewal_info')}</h2>
          <div className={[styles['edit-remove-btn']]}>
            {permitInfo.paymentAmountFull ? null : (
              <button
                className={['btn-xs btn-default', styles['edit_button_right']].join(' ')}
                onClick={editPermitOnClick}
                value={t('permits_list.edit')}
                name={t('permits_list.edit')}
              >
                {t('permits_list.edit')}
              </button>
              // <a
              //   href="javascript:void(0);"
              //   className={[styles['edit_button_right']].join(' ')}
              //   onClick={editPermitOnClick}
              //   value={t('permits_list.edit')}
              //   name={t('permits_list.edit')}
              // >
              //   {t('permits_list.edit')}
              // </a>
            )}
            <button
              className={['btn-xs btn-secondary', styles['remove_button_right']].join(' ')}
              onClick={removePermitOnClick}
              value={t('permits_list.remove')}
              name={t('permits_list.remove')}
            >
              {t('permits_list.remove')}
            </button>
            {/* <a
              href={
                reviewConfirmation ? `/${i18n.language}${t('routes.review_confirmation')}` : `/${i18n.language}${t('routes.listed_permits')}`
              }
              className={styles['remove_button_right']}
              onClick={removePermitOnClick}
              value={t('permits_list.remove')}
              name={t('permits_list.remove')}
            >
              {t('permits_list.remove')}
            </a> */}
          </div>
        </div>
      </div>
      {/* )} */}

      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.plate_number')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }} ref={permitEl}>
          {permitInfo.plateNumber}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.vin_serial')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {permitInfo.vinSerialNumber}
        </div>
      </div>
      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.renewal_period')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {parsePeriodText(permitInfo.renewalPeriod, i18n.language)}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.renewal_fee')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {getAmountString(renewalAmountStr, i18n.language)}
        </div>
      </div>
      {permitInfo.extraFees &&
        permitInfo.extraFees.map((extraFee, i) => (
          <div className="row row-odd" key={`extra-fee-${i}`}>
            <div className="col-xs-6">
              <strong>{getExtraFeeLabel(extraFee, t)}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {getAmountString((extraFee.extraFeeAmount * 0.01).toFixed(2), i18n.language)}
            </div>
          </div>
        ))}
      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.insurance_company')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {permitInfo.insuranceCompany.toUpperCase()}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.policy_number')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {permitInfo.policyNumber}
        </div>
      </div>
      <div className="row row-odd">
        <div className="col-xs-6">
          <strong>{t('permits_list.effective_date')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {formatDate(permitInfo.effectiveDate, i18n.language)}
        </div>
      </div>
      <div className="row row-even">
        <div className="col-xs-6">
          <strong>{t('permits_list.expiry_date')}</strong>
        </div>
        <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
          {formatDate(permitInfo.expiryDate, i18n.language)}
        </div>
      </div>
      {permitInfo.vehicleType === 'fisherman' && (
        <Fragment>
          <div className="row row-odd">
            <div className="col-xs-6">
              <strong>{t('permits_list.commercial_aquaculture')}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {permitInfo.commercialAquaculture}
            </div>
          </div>
          <div className="row row-even">
            <div className="col-xs-6">
              <strong>{t('permits_list.fishing_vessel')}</strong>
            </div>
            <div className="col-xs-6" style={{ lineBreak: 'anywhere' }}>
              {permitInfo.fishingVessel}
            </div>
          </div>
        </Fragment>
      )}
      {(permitInfo.vehicleType === 'fisherman' || permitInfo.vehicleType === 'farmer') && (
        <div className="row row-odd">
          <div className="col-xs-12">
            <strong>
              {t('permits_list.you_certified')}
              {motorVehicleAct}
              {permitInfo.vehicleType === 'farmer' && t('permits_list.you_certified_farm')}
              {permitInfo.vehicleType === 'fisherman' && t('permits_list.you_certified_fishing')}
            </strong>
          </div>
        </div>
      )}
      {/* <div className="row" style={{ display: 'flex', flexGrow: 1 }}>
        <div className="col-xs-6"><strong>{t('permits_list.insurance_company')}</strong></div>
        <div className="col-xs-6" style={{ lineBreak: "anywhere" }}>{permitInfo.insuranceCompany}</div>
      </div>
      <hr /> */}
    </Fragment>

    // <tbody style={{ border: '1px solid black' }}>
    //   <tr>
    //     <td colSpan="5" >
    //       <div style={{ display: "flex", justifyContent: "flex-end", marginRight: "1rem" }}>
    //         {permitInfo.paymentAmountFull ? null : (
    //           <button
    //             className={['btn-xs btn-default', styles['edit_button']].join(' ')}
    //             onClick={editPermitOnClick}
    //             value={t('permits_list.edit')}
    //             name={t('permits_list.edit')}
    //           >
    //             {t('permits_list.edit')}
    //           </button>
    //         )}
    //         <a
    //           href={reviewConfirmation ? `/${i18n.language}${t('routes.review_confirmation')}` : `/${i18n.language}${t('routes.listed_permits')}`}
    //           className={styles['remove_button']}
    //           onClick={removePermitOnClick}
    //           value={t('permits_list.remove')}
    //           name={t('permits_list.remove')}
    //         >
    //           {t('permits_list.remove')}
    //         </a>
    //       </div>
    //     </td>
    //   </tr>
    //   <tr>
    //     <td colSpan="3"><strong>{t('permits_list.plate_number')}</strong></td>
    //     <td colSpan="2" ref={permitEl}>{permitInfo.plateNumber}</td>
    //   </tr>
    //   <tr>
    //     <td colSpan="3"><strong>{t('permits_list.vin_serial')}</strong></td>
    //     <td colSpan="2">{permitInfo.vinSerialNumber}</td>
    //   </tr>
    //   {/* renewal period */}
    //   <tr>
    //     <td colSpan="3"><strong>{t('permits_list.renewal_period')}</strong></td>
    //     <td colSpan="2">{parsePeriodText(permitInfo.renewalPeriod, i18n.language)}</td>
    //   </tr>
    //   {/* renewal fee */}
    //   <tr>
    //     <td colSpan="3"><strong>{t('permits_list.renewal_fee')}</strong></td>
    //     <td colSpan="2">{getAmountString(renewalAmountStr)}</td>
    //   </tr>

    //   {/* insurance info */}
    //   {permitInfo.insuranceCompany && (
    //     <tr>
    //       <td colSpan="3"><strong>{t('permits_list.insurance_company')}</strong></td>
    //       <td colSpan="2" style={{ lineBreak: "anywhere" }}>{permitInfo.insuranceCompany}</td>
    //     </tr>
    //   )}

    //   {/* policy number */}
    //   {permitInfo.policyNumber && (
    //     <tr>
    //       <td colSpan="3"><strong>{t('permits_list.policy_number')}</strong></td>
    //       <td colSpan="2" style={{ lineBreak: "anywhere" }}>{permitInfo.policyNumber}</td>
    //     </tr>
    //   )}

    //   {/* Effective date */}
    //   {permitInfo.effectiveDate && (
    //     <tr>
    //       <td colSpan="3"><strong>{t('permits_list.effective_date')}</strong></td>
    //       <td colSpan="2">{formatDate(permitInfo.effectiveDate, i18n.language)}</td>
    //     </tr>
    //   )}

    //   {permitInfo.expiryDate && (
    //     <tr>
    //       <td colSpan="3"><strong>{t('permits_list.expiry_date')}</strong></td>
    //       <td colSpan="2">{formatDate(permitInfo.expiryDate, i18n.language)}</td>
    //     </tr>
    //   )}

    //   {
    //     permitInfo.vehicleType === 'fisherman' ?
    //       (
    //         <React.Fragment>
    //           {
    //             permitInfo.commercialAquaculture ? (
    //               <tr>
    //                 <td colSpan="3"><strong>{t('permits_list.commercial_aquaculture')}</strong></td>
    //                 <td colSpan="2">{permitInfo.commercialAquaculture}</td>
    //               </tr>
    //             ) : null
    //           }
    //           {
    //             permitInfo.fishingVessel ? (
    //               <tr>
    //                 <td colSpan="3"><strong>{t('permits_list.fishing_vessel')}</strong></td>
    //                 <td colSpan="2">{permitInfo.fishingVessel}</td>
    //               </tr>
    //             ) : null
    //           }
    //         </React.Fragment>
    //       ) : null
    //   }

    //   {
    //     permitInfo.vehicleType === 'fisherman' || permitInfo.vehicleType === 'farmer' ?
    //       (
    //         <tr>
    //           <td colSpan="5">
    //             <strong>
    //               {t('permits_list.you_certified')}{motorVehicleAct}
    //               {
    //                 permitInfo.vehicleType === 'farmer' ? t('permits_list.you_certified_farm') : null
    //               }
    //               {
    //                 permitInfo.vehicleType === 'fisherman' ? t('permits_list.you_certified_fishing') : null
    //               }
    //             </strong>
    //           </td>
    //         </tr>
    //       ) : null
    //   }
    // </tbody >
  )
}

export default withTranslation('common')(PermitsList)
